import React from 'react';
import { EyeIcon, LockClosedIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Visibility, type VisibilityConfigFieldsFragment } from '~/api/graphql';

/**
 * Visual indicator for an object's visibility level.
 *
 * The list variant renders nothing for PUBLIC rows so the dominant case
 * stays visually quiet — list pages only call out the items that are
 * actually restricted. The detail variant always renders so the user can
 * read the current state at a glance.
 */
export type VisibilityBadgeVariant = 'list' | 'detail' | 'compact';

interface VisibilityBadgeProps {
  visibility: VisibilityConfigFieldsFragment | null | undefined;
  variant?: VisibilityBadgeVariant;
  className?: string;
}

const labelFor = (level: Visibility) => {
  switch (level) {
    case Visibility.Private:
      return 'Private';
    case Visibility.Shared:
      return 'Shared';
    case Visibility.Public:
      return 'Public';
    default:
      return 'Public';
  }
};

const iconFor = (level: Visibility, variant: VisibilityBadgeVariant) => {
  switch (level) {
    case Visibility.Private:
      return LockClosedIcon;
    case Visibility.Shared:
      return UserGroupIcon;
    default:
      // List variant returns null so PUBLIC rows render nothing at all
      // (handled by the caller). Detail and compact variants always want
      // a glyph so the pill reads as an affordance, not bare text/space.
      return variant === 'list' ? null : EyeIcon;
  }
};

export const VisibilityBadge: React.FC<VisibilityBadgeProps> = ({
  visibility,
  variant = 'list',
  className = ''
}) => {
  const level = visibility?.level ?? Visibility.Public;

  // List variant: PUBLIC is the dominant case; rendering nothing keeps tables uncluttered.
  if (variant === 'list' && level === Visibility.Public) {
    return null;
  }

  const Icon = iconFor(level, variant);
  const label = labelFor(level);

  if (variant === 'list') {
    // Icon-only with a tooltip-friendly title attribute. Avoids consuming
    // table column width.
    return Icon ? (
      <span
        title={label}
        className={`inline-flex items-center text-neutral-500 dark:text-neutral-400 ${className}`}
      >
        <Icon className="h-4 w-4" aria-hidden={false} role="img" aria-label={label} />
      </span>
    ) : null;
  }

  if (variant === 'compact') {
    // Icon-only with a hover label for tight UIs (e.g. chat compose row)
    // where the surrounding context makes the meaning obvious and the text
    // label is just noise. Always renders, including for PUBLIC.
    return Icon ? (
      <span title={label} className={`inline-flex items-center text-current ${className}`}>
        <Icon className="h-4 w-4" aria-hidden={false} role="img" aria-label={label} />
      </span>
    ) : null;
  }

  // Detail variant: text + icon, suitable for headers and detail pages.
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-neutral-700 dark:text-neutral-300 ${className}`}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" aria-hidden="true" /> : null}
      <span>{label}</span>
    </span>
  );
};
